import { createSortFormForPosition } from 'utils/helpers/expression-utils';
import { getOvertimeWinner } from 'utils/game-ot-utils';
import * as winType from 'constants/win-type';
import * as teamStatusType from 'constants/team-status-type';

export const TIME_FILTERS = {
    HOURS_24: '24h',
    DAYS_7: '7d',
    DAYS_30: '30d',
    CUSTOM: 'custom'
};

export const ACTIVITY_TYPES = {
    TEAM: 'team',
    PLAYER: 'player'
};

export const ACTIVITY_PAGE_SIZE = 8;
export const ACTIVITY_GAMES_PER_ENTITY = 1000;

export const buildGameIntelligenceUrl = ({ gameId, teamId }) => {
    if (!gameId || !teamId) return null;

    return `/game-intelligence?gameId=${gameId}&teamId=${teamId}`;
};

export const buildPlayerIntelligenceUrl = ({ gameId, playerId, isGoalie }) => {
    if (!gameId || !playerId) return null;
    const path = isGoalie ? 'goalie-intelligence' : 'skater-intelligence';

    return `/${path}?gameId=${gameId}&playerId=${playerId}`;
};

export const FAVORITE_FILTERS = {
    ALL: 'all',
    PLAYERS: 'players',
    TEAMS: 'teams'
};

const MS_HOUR = 60 * 60 * 1000;
const MS_DAY = 24 * MS_HOUR;

const startOfDay = date => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);

    return next;
};

const endOfDay = date => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);

    return next;
};

const padDatePart = value => String(value).padStart(2, '0');

const getLocaleCode = locale => {
    if (locale === 'ru') return 'ru-RU';
    if (locale === 'zh') return 'zh-CN';

    return 'en-US';
};

const getPlayerGameScores = (game, playerTeamId) => {
    if (!game || !playerTeamId) return null;
    const homeId = game.homeTeamId || game.homeTeam?.id;
    const awayId = game.awayTeamId || game.awayTeam?.id;
    const isHome = playerTeamId === homeId;
    const isAway = playerTeamId === awayId;
    if (!isHome && !isAway) return null;
    if (game.homeScore == null || game.awayScore == null) return null;
    const playerScore = isHome ? game.homeScore : game.awayScore;
    const opponentScore = isHome ? game.awayScore : game.homeScore;

    return { playerScore, opponentScore, isHome, isAway };
};

export const getFilterDateRange = (filter, customRange) => {
    const now = new Date();

    // eslint-disable-next-line no-restricted-syntax -- TODO: legacy forEach/switch (baseline)
    switch (filter) {
        case TIME_FILTERS.HOURS_24:
            return { begin: new Date(now.getTime() - MS_DAY), end: now };
        case TIME_FILTERS.DAYS_7:
            return { begin: startOfDay(new Date(now.getTime() - MS_DAY * 6)), end: now };
        case TIME_FILTERS.DAYS_30:
            return { begin: startOfDay(new Date(now.getTime() - MS_DAY * 29)), end: now };
        case TIME_FILTERS.CUSTOM:
            if (!customRange?.from) return null;

            return {
                begin: startOfDay(customRange.from),
                end: endOfDay(customRange.to || customRange.from)
            };
        default:
            return { begin: new Date(now.getTime() - MS_DAY), end: now };
    }
};

export const toApiDate = date => {
    if (!date) return '';
    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed.getTime())) return '';
    const year = parsed.getFullYear();
    const month = padDatePart(parsed.getMonth() + 1);
    const day = padDatePart(parsed.getDate());

    return `${year}-${month}-${day}`;
};

export const toApiDateRange = range => {
    if (!range?.begin) return null;
    const begin = toApiDate(range.begin);
    const end = toApiDate(range.end || range.begin);
    if (!begin || !end) return null;

    return { begin, end };
};

export const getDateRangeKey = range => {
    const apiRange = toApiDateRange(range);

    return apiRange ? `${apiRange.begin}_${apiRange.end}` : false;
};

const formatLocalDate = (date, locale = 'en') => {
    const month = padDatePart(date.getMonth() + 1);
    const day = padDatePart(date.getDate());
    const year = date.getFullYear();

    if (locale === 'ru' || locale === 'zh') {
        return `${day}/${month}/${year}`;
    }

    return `${month}/${day}/${year}`;
};

export const formatCustomRangeLabel = (range, locale = 'en') => {
    if (!range?.from) return '';
    const fromLabel = formatLocalDate(range.from, locale);
    if (!range.to || range.from.toDateString() === range.to.toDateString()) {
        return fromLabel;
    }

    return `${fromLabel} – ${formatLocalDate(range.to, locale)}`;
};

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// A date without time is a calendar day: `new Date('2026-10-01')` is UTC midnight, which is
// Sep 30 in North American time zones. Parse it as local midnight instead.
const parseGameDate = raw => {
    const dateOnly = typeof raw === 'string' && raw.match(DATE_ONLY_RE);

    return dateOnly ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])) : new Date(raw);
};

export const getGameTimestamp = game => {
    const raw = game?.dateTime || game?.date;
    if (!raw) return 0;
    const parsed = parseGameDate(raw);

    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

export const isGameInRange = (game, range) => {
    const timestamp = getGameTimestamp(game);
    if (!timestamp || !range?.begin || !range?.end) return false;

    return timestamp >= range.begin.getTime() && timestamp <= range.end.getTime();
};

export const getPlayerPositionCode = player => {
    const raw = player?.playerPosition || player?.params?.playerPosition || player?.position || '';
    if (!raw) return '';

    return createSortFormForPosition(raw) || raw;
};

export const getSeasonTypeId = seasonName => {
    if (!seasonName) return null;
    if (/playoff/i.test(seasonName)) return 'home.activity.season.playoffs';
    if (/pre-?season|exhibition/i.test(seasonName)) return 'home.activity.season.preseason';
    if (/regular/i.test(seasonName)) return 'home.activity.season.regular';

    return null;
};

export const getGameStatusId = (game, { playerTeamId } = {}) => {
    if (!game) return null;

    const otWinner = getOvertimeWinner(game);
    let suffixId = 'home.activity.status.final';
    if (otWinner.teamStatus !== teamStatusType.NONE) {
        if (otWinner.type === winType.SO) suffixId = 'home.activity.status.finalSO';
        else if (otWinner.type === winType.OT) suffixId = 'home.activity.status.finalOT';
    }

    const scores = getPlayerGameScores(game, playerTeamId);
    if (!scores) return suffixId;

    if (scores.playerScore > scores.opponentScore) return 'home.activity.status.win';
    if (scores.playerScore < scores.opponentScore) return 'home.activity.status.loss';

    return suffixId;
};

export const formatScore = (game, { playerTeamId } = {}) => {
    const scores = getPlayerGameScores(game, playerTeamId);
    if (scores) return `${scores.playerScore} - ${scores.opponentScore}`;
    if (game?.homeScore != null && game?.awayScore != null) {
        return `${game.homeScore} - ${game.awayScore}`;
    }

    return '–';
};

export const formatGameDate = (game, locale = 'en') => {
    const timestamp = getGameTimestamp(game);
    if (!timestamp) return '';

    return new Date(timestamp).toLocaleDateString(getLocaleCode(locale), {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

export const formatGameTime = (game, locale = 'en') => {
    if (!game?.dateTime) return '';
    const timestamp = getGameTimestamp(game);
    if (!timestamp) return '';

    return new Date(timestamp).toLocaleTimeString(getLocaleCode(locale), {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
    });
};

export const formatRelativeTime = (timestamp, { now = Date.now(), locale = 'en' } = {}) => {
    if (!timestamp) return { id: 'home.activity.relative.justNow' };

    const diff = now - timestamp;
    if (diff < MS_HOUR) return { id: 'home.activity.relative.justNow' };

    const hours = Math.floor(diff / MS_HOUR);
    if (hours < 24) {
        return { id: 'home.activity.relative.hoursAgo', values: { hours } };
    }

    return {
        text: new Date(timestamp).toLocaleDateString(getLocaleCode(locale), {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        })
    };
};

export const getLeagueName = (seasonId, seasonsById, leaguesById) => {
    const season = seasonsById?.[seasonId];
    if (!season) return '';
    const league = leaguesById?.[season.leagueId];

    return league?.acronymEn || league?.nameEn || '';
};

export const getSeasonName = (game, seasonsById) => game?.season?.nameEn || seasonsById?.[game?.seasonId]?.nameEn || '';

export const getInitials = name =>
    (name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase();
